import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment-timezone';
import axios from 'axios';

import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import SecondaryButton from './lib/buttons/SecondaryButton';
import { Loading } from '../stories/Loading';
import { FeatureContext } from '../App';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  mainContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
    },
    borderRadius: '4px',
    marginBottom: '12px',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '12px',
  },
  formSeparator: {
    background: '#004878',
    width: '2px',
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '2px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '2px',
      height: 'auto',
    },
  },
  button: {
    maxWidth: '320px',
    maxHeight: '60px',
    width: '100%',
    marginBottom: '24px',
    marginTop: '24px',
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'center',
    },
  },
  guestInfoTable: {
    flex: 1,
  },
  guestInfoSeparator: {
    background: '#004878',
    width: '2px',
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  guestInfoDataKey: {
    width: '120px',
    color: '#004878',
    fontSize: '18px',
    fontWeight: 400,
    padding: '8px',
    verticalAlign: 'top',
  },
  guestInfoDataValue: {
    color: '#004878',
    fontSize: '18px',
    fontWeight: 700,
    padding: '8px',
    verticalAlign: 'top',
  },
}));

interface UpgradeOccupancyProps {}

const UpgradeOccupancy = (props: UpgradeOccupancyProps) => {
  const classes = useStyles();
  const features = useContext(FeatureContext);

  const [loading, setLoading] = useState(false);
  const [checkedRooms, setCheckedRooms] = useState<any[]>(new Array<any>());
  const [selectedRoom, setSelectedRoom] = useState<any>();

  useEffect(() => {
    getAllCheckedRooms();
  }, []);

  const getAllCheckedRooms = () => {
    setLoading(true);

    axios
      .get('/api/getAllCheckedRooms')
      .then((res) => res.data)
      .then((result) => {
        if (result.err) {
          alert(result.err);
        } else {
          if (result.checkedRooms && result.checkedRooms.length > 0) {
            let soRooms = result.checkedRooms.filter(
              (r: any) => r.ref_booking_id === '-SO-'
            );
            setCheckedRooms(soRooms);
          } else {
            setCheckedRooms([]);
          }
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  const performRoomUpgrade = () => {
    if (selectedRoom) {
      setLoading(true);
      axios
        .post('/api/upgradeRoomOccupancy', {
          checkinInfoId: selectedRoom.checkin_info_id,
        })
        .then((res) => res.data)
        .then((result) => {
          // console.log(result);
          if (result.errno > 0) {
            alert(result.msg || 'Failed to upgrade room!');
          } else {
            alert('Upgrade Success!');
            getAllCheckedRooms();
          }
        })
        .catch((err) => {
          console.log(err);
          alert('Failed to upgrade the room');
        })
        .finally(() => {
          setLoading(false);
          reset();
        });
    } else {
      alert('No Room Selected!');
    }
  };

  const reset = () => {
    setSelectedRoom(undefined);
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>Room Upgrade</h2>
        <hr className={classes.separatorLine} />
        <div className={classes.mainContainer}>
          <div className={classes.formContainer}>
            <Autocomplete
              options={checkedRooms}
              groupBy={(r: any) =>
                features.groupRoomsByBlockFeatureEnabled
                  ? r.block_name
                  : r.floor_str
              }
              getOptionLabel={(r: any) => `${r.room_name} - ${r.room_type}`}
              style={{ width: 300, marginTop: '24px', marginBottom: '24px' }}
              renderInput={(params: { [x: string]: any }) => (
                <TextField {...params} label="Room #" variant="outlined" />
              )}
              onChange={(_, r) => {
                if (r != null) {
                  setSelectedRoom(r);
                } else {
                  setSelectedRoom(undefined);
                }
              }}
              value={selectedRoom || null}
            />
            <SecondaryButton
              onClick={performRoomUpgrade}
              className={classes.button}
            >
              Upgrade
            </SecondaryButton>
          </div>
          <hr className={classes.formSeparator} />
          <div className={classes.formContainer}>
            <label
              style={{
                margin: '8px',
                marginBottom: '32px',
                textDecoration: 'underline',
                color: '#004878',
                fontSize: '18px',
                fontWeight: 700,
                padding: '8px',
                verticalAlign: 'top',
              }}
            >
              Guest Info
            </label>
            <table className={classes.guestInfoTable}>
              <tbody>
                <tr>
                  <td className={classes.guestInfoDataKey}>Name:</td>
                  <td className={classes.guestInfoDataValue}>
                    {selectedRoom?.first_name} {selectedRoom?.last_name}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Phone No:</td>
                  <td className={classes.guestInfoDataValue}>
                    {selectedRoom?.contact}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>GST No:</td>
                  <td className={classes.guestInfoDataValue}>
                    {selectedRoom?.gst_number}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Checkin Date:</td>
                  <td className={classes.guestInfoDataValue}>{`${
                    selectedRoom?.checkin_date
                      ? moment(selectedRoom?.checkin_date).format('DD/MM/YY') +
                        '  -  '
                      : ''
                  } ${selectedRoom?.checkin_time || ''}`}</td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Register No:</td>
                  <td className={classes.guestInfoDataValue}>
                    {selectedRoom?.checkin_no}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
};

export default UpgradeOccupancy;
